import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fileToDataUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toDisplay } from "../lib/roles";
import { Field, PageHeader, Panel } from "../components/common";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

export default function NewLeave() {
  const { user } = useAuth();
  const nav = useNavigate();
  const tipi = ["Ferie", "Permesso", ...(user?.legge_104 ? ["Permesso L.104"] : []), "Malattia"];
  const [tipo, setTipo] = useState("Ferie");
  const [f, setF] = useState({ inizio: "", fine: "", giorno: "", oraInizio: "", oraFine: "", motivo: "" });
  const [allegato, setAllegato] = useState(null);
  const [loading, setLoading] = useState(false);
  const isPermesso = tipo.startsWith("Permesso");
  const isMalattia = tipo === "Malattia";
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Il file supera i 2 MB");
    setAllegato({ data: await fileToDataUrl(file), name: file.name, mime: file.type });
  };

  const submit = async () => {
    let body = { tipo, motivo: f.motivo };
    if (isPermesso) {
      if (!f.giorno || !f.oraInizio || !f.oraFine) return toast.error("Indica giorno e orario del permesso");
      body = { ...body, data_inizio: toDisplay(f.giorno), data_fine: toDisplay(f.giorno), ora_inizio: f.oraInizio, ora_fine: f.oraFine };
    } else {
      if (!f.inizio || !f.fine) return toast.error(`Indica le date di inizio e fine ${isMalattia ? "malattia" : "ferie"}`);
      body = { ...body, data_inizio: toDisplay(f.inizio), data_fine: toDisplay(f.fine) };
      if (isMalattia) body = { ...body, allegato: allegato?.data || "", allegato_nome: allegato?.name || "", allegato_tipo: allegato?.mime || "" };
    }
    setLoading(true);
    try {
      await api("/leaves", { method: "POST", body });
      toast.success(isMalattia ? "Comunicazione di malattia inviata" : "Richiesta inviata");
      nav("/turni");
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl">
      <PageHeader subtitle="Ferie e permessi" title="Nuova Richiesta" />
      <Panel className="space-y-6">
        <div className="flex flex-wrap gap-2">{tipi.map((t) => (
          <button key={t} onClick={() => setTipo(t)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${tipo === t ? "bg-[var(--navy)] text-white border-[var(--navy)]" : "bg-white hover:border-[var(--teal)]"}`} data-testid={`leave-type-${t.replace(/[\s.]/g, "-").toLowerCase()}`}>{t}</button>))}
        </div>
        {isPermesso ? (
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Giorno del permesso"><Input type="date" value={f.giorno} onChange={set("giorno")} data-testid="leave-day-input" /></Field>
            <Field label="Ora inizio"><Input type="time" value={f.oraInizio} onChange={set("oraInizio")} data-testid="leave-start-time-input" /></Field>
            <Field label="Ora fine"><Input type="time" value={f.oraFine} onChange={set("oraFine")} data-testid="leave-end-time-input" /></Field>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Data inizio"><Input type="date" value={f.inizio} onChange={set("inizio")} data-testid="leave-start-date-input" /></Field>
            <Field label="Data fine"><Input type="date" value={f.fine} onChange={set("fine")} data-testid="leave-end-date-input" /></Field>
          </div>
        )}
        {isMalattia && <Field label="Certificato medico (immagine o PDF, max 2 MB)"><Input type="file" accept="image/*,application/pdf" onChange={onFile} data-testid="leave-attachment-input" /></Field>}
        <Field label="Motivo (facoltativo)"><Textarea value={f.motivo} onChange={set("motivo")} placeholder="Motivazione..." data-testid="leave-reason-input" /></Field>
        <p className="text-sm text-slate-500">{isPermesso ? "Il permesso deve rientrare nel tuo orario di turno di quel giorno." : isMalattia ? "Indica il periodo di malattia e, se disponibile, allega il certificato medico." : "Le ferie vanno richieste almeno 2 giorni prima."}</p>
        <Button onClick={submit} disabled={loading} className="rounded-full bg-[var(--teal)] hover:bg-[#257474] px-8" data-testid="leave-submit-button">{isMalattia ? "Invia Comunicazione" : "Invia Richiesta"}</Button>
      </Panel>
    </div>
  );
}
